import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface LeftSidebarMenuProps {
  isHomeMenu?: boolean;
  isReportMenu?: boolean;
  isGoalMenu?: boolean;
  isAdjustMenu?: boolean;
  urlForHomeMenu?: string;
  urlForReportMenu?: string;
  urlForGoalMenu?: string;
  editUserRegistrationPath?: string;
  isApiUser?: boolean;
  // Tipo de página para cor do gradiente
  pageType?: 'water' | 'gas' | 'energy';
  // Drawer mobile: aberto/fechado, fechar e sair
  isOpen?: boolean;
  onClose?: () => void;
  onLogout?: () => void;
}

// Gradientes por tipo de página
const sidebarGradients = {
  water: 'linear-gradient(to bottom, rgba(79,209,214,1) 0%, rgba(44,141,179,1) 100%)',
  gas: 'linear-gradient(to bottom, rgba(249,163,62,1) 0%, rgba(246,105,79,1) 100%)',
  energy: 'linear-gradient(45deg, #b4dc1a 0%, #71ce19 100%)',
};

/**
 * LeftSidebarMenu - Menu lateral esquerdo
 * 
 * Sidebar com gradiente que muda baseado no tipo de página (water/gas/energy).
 * O gradiente agora é aplicado inline via prop pageType.
 * 
 * Cada item tem dois ícones que alternam no hover/current (opacity transition).
 * O sprite está em /images/icons-set.png com posições específicas para cada ícone.
 */
export function LeftSidebarMenu({
  isHomeMenu = false,
  isReportMenu = false,
  isGoalMenu = false,
  isAdjustMenu = false,
  urlForHomeMenu = '',
  urlForReportMenu = '',
  urlForGoalMenu = '',
  editUserRegistrationPath = '',
  isApiUser = false,
  pageType = 'water',
  isOpen = false,
  onClose,
  onLogout,
}: LeftSidebarMenuProps) {
  const { t } = useTranslation();

  // Classes base para links do menu
  const linkBaseClasses = `
    has-arrow
    relative block
    !text-white text-center text-[12px] font-medium
    py-[20px] px-0 pb-[7px]
    whitespace-nowrap
    group
    [text-shadow:0_1px_2px_rgba(0,0,0,0.3)]
  `;

  // Classes para ícone "one" (estado normal)
  const icoOneBaseClasses = `
    ico one
    block mx-auto
    w-[31px] h-[30px]
    bg-[url('/images/icons-set.png')] bg-no-repeat
    transition-opacity duration-300
  `;

  // Classes para ícone "two" (estado hover/current)
  const icoTwoBaseClasses = `
    ico two
    absolute left-[37px] top-[20px]
    w-[31px] h-[30px]
    bg-[url('/images/icons-set.png')] bg-no-repeat
    opacity-0 transition-opacity duration-300
    group-hover:opacity-100
  `;

  return (
    <div
      className={`
        fixed w-[100px] h-full top-0 left-0
        z-20 pt-[90px]
        shadow-sidebar
        transition-[transform,visibility] duration-200
        max-[812px]:z-[60] max-[812px]:pt-2
        ${isOpen ? 'translate-x-0' : 'max-[812px]:-translate-x-full max-[812px]:invisible'}
      `}
      style={{ background: sidebarGradients[pageType] }}
    >
      {/* Fechar drawer (somente mobile) */}
      <button
        type="button"
        onClick={onClose}
        aria-label={t('views.layouts.menu.close_menu')}
        className="hidden max-[812px]:flex items-center justify-center w-[44px] h-[44px] ml-auto mr-[8px] bg-transparent border-0 cursor-pointer text-white"
      >
        <i className="mdi mdi-close text-[24px]"></i>
      </button>

      {/* Sidebar scroll */}
      <div className="scroll-sidebar pb-[60px]">
        {/* Sidebar navigation */}
        <nav className="sidebar-nav p-0">
          <ul id="sidebarnav" className="m-0 p-0">
            {/* Divider */}
            <li className="h-[1px] bg-[rgba(120,130,140,0.13)] block"></li>
            
            {/* Home - primeiro item com margin-top extra */}
            <li className="one mb-[5px] mt-[30px] max-[812px]:mt-[10px]">
              <Link 
                className={`
                  ${linkBaseClasses}
                  home
                  ${isHomeMenu ? 'current' : ''}
                `} 
                to={urlForHomeMenu} 
                aria-expanded="false"
              >
                {/* Ícone normal */}
                <i className={`
                  ${icoOneBaseClasses}
                  bg-[1px_0]
                  ${isHomeMenu ? 'opacity-0' : 'opacity-100'}
                  group-hover:opacity-0
                `}></i>
                {/* Ícone hover/current */}
                <i className={`
                  ${icoTwoBaseClasses}
                  bg-[1px_-28px]
                  ${isHomeMenu ? '!opacity-100' : ''}
                `}></i>
                <span className="hide-menu">{t('views.layouts.menu.home', 'Home')}</span>
              </Link>
            </li>

            {!isApiUser && (
              <>
                {/* Relatórios */}
                <li className="mb-[5px]">
                  <Link 
                    className={`
                      ${linkBaseClasses}
                      reports
                      ${isReportMenu ? 'current' : ''}
                    `} 
                    to={urlForReportMenu} 
                    aria-expanded="false"
                  >
                    <i className={`
                      ${icoOneBaseClasses}
                      bg-[-30px_0]
                      ${isReportMenu ? 'opacity-0' : 'opacity-100'}
                      group-hover:opacity-0
                    `}></i>
                    <i className={`
                      ${icoTwoBaseClasses}
                      bg-[-30px_-28px]
                      ${isReportMenu ? '!opacity-100' : ''}
                    `}></i>
                    <span className="hide-menu">{t('views.consumption.report.title', 'Relatórios')}</span>
                  </Link>
                </li>

                {/* Sua meta */}
                <li className="mb-[5px]">
                  <Link 
                    className={`
                      ${linkBaseClasses}
                      your-goals
                      ${isGoalMenu ? 'current' : ''}
                    `} 
                    to={urlForGoalMenu} 
                    aria-expanded="false"
                  >
                    <i className={`
                      ${icoOneBaseClasses}
                      bg-[-60px_0]
                      ${isGoalMenu ? 'opacity-0' : 'opacity-100'}
                      group-hover:opacity-0
                    `}></i>
                    <i className={`
                      ${icoTwoBaseClasses}
                      bg-[-60px_-29px]
                      ${isGoalMenu ? '!opacity-100' : ''}
                    `}></i>
                    <span className="hide-menu">{t('views.consumption.goal.title', 'Sua meta')}</span>
                  </Link>
                </li>
              </>
            )}

            {/* Ajustes */}
            <li className="mb-[5px]">
              <Link 
                className={`
                  ${linkBaseClasses}
                  settings
                  ${isAdjustMenu ? 'current' : ''}
                `} 
                to={editUserRegistrationPath} 
                aria-expanded="false"
              >
                <i className={`
                  ${icoOneBaseClasses}
                  bg-[-123px_0]
                  ${isAdjustMenu ? 'opacity-0' : 'opacity-100'}
                  group-hover:opacity-0
                `}></i>
                <i className={`
                  ${icoTwoBaseClasses}
                  bg-[-123px_-29px]
                  ${isAdjustMenu ? '!opacity-100' : ''}
                `}></i>
                <span className="hide-menu">{t('views.devise.registrations.adjustments', 'Ajustes')}</span>
              </Link>
            </li>

            {/* Sair (somente mobile — no desktop fica no dropdown do topo) */}
            <li className="hidden max-[812px]:block mb-[5px]">
              <button
                type="button"
                onClick={onLogout}
                className={`${linkBaseClasses} w-full min-h-[44px] bg-transparent border-0 cursor-pointer`}
              >
                <i className="fa fa-power-off block text-[22px] text-white"></i>
                <span className="hide-menu">{t('views.application.exit', 'Sair')}</span>
              </button>
            </li>
          </ul>
        </nav>
        {/* End Sidebar navigation */}
      </div>
      {/* End Sidebar scroll */}
    </div>
  );
}
